import type { PlaybackState } from "../timeline/playback";
import type { TimelineGpuScene } from "../timeline/board/ChapterBoard/gpuScene";
import { buildTimelineVertices, countTimelineVertices } from "./build-vertices";
import type { TimelinePipeline } from "./create-pipeline";

export interface TimelineViewState {
  panX: number;
  panY: number;
  zoom: number;
}

function canvasPixelRatio(canvas: HTMLCanvasElement): number {
  const clientW = Math.max(1, canvas.clientWidth);
  return canvas.width / clientW;
}

/** The scene as triangles, ready for whichever renderer is drawing it. */
export function timelineVerticesFor(
  canvas: HTMLCanvasElement,
  scene: TimelineGpuScene,
  playback: PlaybackState,
  viewportWidth: number,
  view: TimelineViewState,
): Float32Array {
  return buildTimelineVertices(
    scene,
    playback.playheadMs,
    Math.max(1, canvas.clientWidth),
    Math.max(1, canvas.clientHeight),
    viewportWidth,
    view.panX,
    view.panY,
    view.zoom,
    canvasPixelRatio(canvas),
  );
}

export function drawTimelineFrame(
  canvas: HTMLCanvasElement,
  context: GPUCanvasContext,
  device: GPUDevice,
  resources: TimelinePipeline,
  scene: TimelineGpuScene,
  playback: PlaybackState,
  viewportWidth: number,
  view: TimelineViewState,
): void {
  const vertexData = timelineVerticesFor(canvas, scene, playback, viewportWidth, view);
  const vertexCount = countTimelineVertices(vertexData);

  device.queue.writeBuffer(
    resources.uniformBuffer,
    0,
    new Float32Array([canvas.width, canvas.height]),
  );
  if (vertexCount > 0) {
    device.queue.writeBuffer(
      resources.vertexBuffer,
      0,
      vertexData.buffer,
      vertexData.byteOffset,
      vertexData.byteLength,
    );
  }

  const encoder = device.createCommandEncoder({ label: "timeline-frame" });
  const pass = encoder.beginRenderPass({
    label: "timeline-pass",
    colorAttachments: [
      {
        view: context.getCurrentTexture().createView(),
        clearValue: { r: 0.03, g: 0.04, b: 0.07, a: 1 },
        loadOp: "clear",
        storeOp: "store",
      },
    ],
  });
  pass.setPipeline(resources.pipeline);
  pass.setBindGroup(0, resources.bindGroup);
  if (vertexCount > 0) {
    pass.setVertexBuffer(0, resources.vertexBuffer);
    pass.draw(vertexCount);
  }
  pass.end();
  device.queue.submit([encoder.finish()]);
}
